import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import type { APIData, Service, Timeframe } from 'webapp-types';
import { ForecastComparisonPeriod, GranulationTypeId } from 'webapp/enums/enums';
import { TrackingEvent } from 'webapp/enums/tracking';

import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import ForecastsTimeframeDropdownComponent from 'webapp/components/forecasts/timeframe/dropdown';
import FormSelectComponent from 'webapp/components/form/select';

import type Forecast from 'webapp/models-custom/forecast';

const TRACKING_ORIGIN = 'forecast';

interface Args {
	forecast: Forecast;
	snapshots: APIData.Forecast.Snapshot[];
	currentSnapshotId?: number | null;
	isSetupLocked?: boolean;
	isForecastSaved?: boolean;
	isRecalculateDisabled?: boolean;
	onSettingsChange: () => void;
	onSnapshotChange: (snapshotId: number) => void;
	onRecalculate: () => void;
}

export default class MetricDetailsForecastsTopbarComponent extends Component<Args> {
	@service('model-manager/forecasts') declare mmForecastsService: Service.ModelManager.Forecasts;
	@service('analytics') declare analyticsService: Service.Analytics;

	get selectedGranulationTypeId(): GranulationTypeId | null {
		if (this.args.forecast.granulation !== undefined) {
			return this.mmForecastsService.timeUnitToGranulationTypeId(
				this.args.forecast.granulation,
			);
		}
		return GranulationTypeId.MONTHLY;
	}

	get granulationOptions() {
		return [
			{ value: GranulationTypeId.DAILY, label: 'Day' },
			{ value: GranulationTypeId.WEEKLY, label: 'Week' },
			{ value: GranulationTypeId.MONTHLY, label: 'Month' },
		];
	}

	get comparisonOptions() {
		return [
			{ value: ForecastComparisonPeriod.PREVIOUS_PERIOD, label: 'Previous period' },
			{ value: ForecastComparisonPeriod.SAME_PERIOD_1Y_AGO, label: 'Same period 1 year ago' },
		];
	}

	get snapshotOptions(): LabelValuePairNumeric[] {
		const snapshots = this.args.snapshots || [];
		const options: LabelValuePairNumeric[] = [];

		snapshots.forEach((snapshot) => {
			const date = new Date(snapshot.timestamp * 1000);
			const formattedDate = date.toLocaleDateString('en-US', {
				month: 'short',
				day: 'numeric',
				year: 'numeric',
			});

			options.push({ value: snapshot.id, label: formattedDate });
		});

		return options;
	}

	@action
	changeTimeframe(timeframe: Timeframe | null, endDate?: string | null): void {
		const forecast = this.args.forecast;
		if (timeframe) {
			forecast.timeframe = timeframe;
		}
		if (endDate !== undefined) {
			forecast.endDate = endDate;
		}
		this.args.onSettingsChange();
		this.analyticsService.event(TrackingEvent.DATE_RANGE_CHANGED, {
			origin: TRACKING_ORIGIN,
		});
	}

	@action
	changeGranulation(value: string | null): void {
		const granulationTypeId = value as GranulationTypeId;
		const timeUnit = this.mmForecastsService.granulationTypeIdToTimeUnit(granulationTypeId);
		if (timeUnit !== null) {
			this.args.forecast.granulation = timeUnit;
			this.args.onSettingsChange();
			this.analyticsService.event(TrackingEvent.GRANULATION_CHANGED, {
				origin: TRACKING_ORIGIN,
			});
		}
	}

	@action
	changeComparisonPeriod(value: string | null): void {
		this.args.forecast.comparisonPeriod = value as ForecastComparisonPeriod;
		this.args.onSettingsChange();
		this.analyticsService.event(TrackingEvent.COMPARE_DROPDOWN_OPENED, {
			origin: TRACKING_ORIGIN,
		});
	}

	<template>
		<div class="block-settings-bar forecast-topbar">
			<div class="block-settings-bar__left">
				<div class="forecast-topbar__timeframe">
					<ForecastsTimeframeDropdownComponent
						@model={{@forecast}}
						@onChange={{this.changeTimeframe}}
						@isDisabled={{@isSetupLocked}}
					/>
				</div>

				{{#if this.snapshotOptions.length}}
					<div class="forecast-topbar__snapshot">
						<FormSelectComponent
							@selected={{@currentSnapshotId}}
							@options={{this.snapshotOptions}}
							@onChange={{@onSnapshotChange}}
							@isRequired={{true}}
							@isNumeric={{true}}
							@showFloatlabel={{false}}
						/>
					</div>
				{{/if}}

				{{#if @isForecastSaved}}
					<button
						type="button"
						class="forecast-topbar__recalculate-btn"
						disabled={{@isRecalculateDisabled}}
						{{on "click" @onRecalculate}}
					>
						<CommonUiIconComponent @name="Refresh" @size="sm" />
						Recalculate
					</button>
				{{/if}}
			</div>

			<div class="block-settings-bar__right">
				<div class="forecast-topbar__granulation">
					<FormSelectComponent
						name="granulation"
						@selected={{this.selectedGranulationTypeId}}
						@options={{this.granulationOptions}}
						@onChange={{this.changeGranulation}}
						@isRequired={{true}}
						@isNumeric={{false}}
						@placeholder="Graph by"
						@isDisabled={{@isSetupLocked}}
					/>
				</div>

				<div class="forecast-topbar__comparison">
					<FormSelectComponent
						name="comparisonPeriod"
						@selected={{@forecast.comparisonPeriod}}
						@options={{this.comparisonOptions}}
						@onChange={{this.changeComparisonPeriod}}
						@isRequired={{true}}
						@isNumeric={{false}}
						@placeholder="Compare to"
					/>
				</div>
			</div>
		</div>
	</template>
}
